// src/components/organisms/PatientWorkspaceTabs/DietEditor.js
//
// Beslenme planı editörü (2026-10-05) — DietPlan kaydına yazar (tedaviden
// bağımsız). Bölümler: plan bilgisi · hedefler · kilo hedefi · öğün planı ·
// serbest/yasaklı besinler · not. Otomatik kayıt (useInlineAutosave).
// Düz editör dili (kutusuz bölümler, drawer ile ekleme): bkz. _editorParts.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, InputAdornment, Typography } from "@mui/material";
import { Restaurant as RestaurantIcon } from "@mui/icons-material";

import FormTextField from "../../atoms/FormTextField";
import FormDatePicker from "../../atoms/FormDatePicker";
import ActionButton from "../../atoms/ActionButton";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import useInlineAutosave from "../../../hooks/useInlineAutosave";
import TagPickerDrawer from "./drawers/TagPickerDrawer";
import { makeFetchPage, pickFoods, foodOption } from "./drawers/fetchers";
import {
  SECTION_SX,
  SECTION_HEADING_SX,
  SPLIT_GRID_SX,
  SPLIT_LEFT_SX,
  SPLIT_RIGHT_SX,
  listAreaSx,
  FieldRow,
  SectionHeader,
  SectionEmpty,
  EditorHeader,
  PdfButton,
} from "./_editorParts";
import { FoodRows, appendFoods } from "./_itemLists";
import MealPlanEditor from "../DietPlan/MealPlanEditor";
import WeightGoalProgress from "../DietPlan/WeightGoalProgress";
import BodyMeasurementsSummary from "../BodyMeasurementsSummary";
import { planToForm, formToPayload, PLAN_MODE_OPTIONS } from "../DietPlan/dietPlanFormat";
import ChoiceChipGroup from "../../atoms/ChoiceChipGroup";

import { useUpdateDietPlanMutation } from "../../../store/secretaryApi";
import {
  useListDoctorDietTemplatesMutation,
  useCreateDoctorDietTemplateMutation,
} from "../../../store/doctorApi";
import { useHasPermission } from "../../../hooks/useHasPermission";
import PromptDialog from "../../molecules/PromptDialog";
import { useAlert } from "../../../context/AlertProvider";
import { useListFoodsMutation } from "../../../store/systemVariablesApi";
import useTerms from "../../../hooks/useTerms";
import useClinicModule from "../../../hooks/useClinicModule";

const FOOD_LIST_HEIGHT = 240;

const foodPickers = (terms) => ({
  allowedList: {
    title: "Serbest Besin Ekle",
    subtitle: `${terms.patientGen} tüketebileceği besinleri seçin`,
    accent: COLORS.successDark,
  },
  forbiddenList: {
    title: "Yasaklı Besin Ekle",
    subtitle: `${terms.patientGen} kaçınması gereken besinleri seçin`,
    accent: COLORS.errorDark,
  },
});

const TARGET_FIELDS = [
  { key: "calories", label: "Enerji", unit: "kcal" },
  { key: "protein", label: "Protein", unit: "g" },
  { key: "carbs", label: "Karbonhidrat", unit: "g" },
  { key: "fat", label: "Yağ", unit: "g" },
  { key: "waterMl", label: "Su", unit: "ml" },
];

// Günlük su hedefi hızlı seçimi (ml) — su modülü açıkken
const WATER_OPTIONS = [1500, 2000, 2500, 3000].map((ml) => ({
  value: String(ml),
  label: `${(ml / 1000).toLocaleString("tr-TR")} L`,
}));

/**
 * Sayı alanı: etiket ÜSTTE küçük ve gri, kutuda yalnız değer, birim sağda soluk.
 * (Eski hâlinde etiket kutunun içinde büyük/koyu duruyordu; değerle ayırt edilmiyor,
 * uzun etiketler kesiliyordu.)
 */
function NumberField({ label, unit, value, onChange, disabled, palette, width = 120 }) {
  return (
    <Box sx={{ width, minWidth: 0 }}>
      <Typography noWrap title={label} sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mb: 0.25 }}>
        {label}
      </Typography>
      <FormTextField
        palette={palette}
        variant="standard"
        size="small"
        type="number"
        fullWidth
        placeholder="—"
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        sx={{ "& input": { fontSize: TYPO.body, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" } }}
        inputProps={{ min: 0, "aria-label": unit ? `${label} (${unit})` : label }}
        InputProps={
          unit
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400 }}>{unit}</Typography>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />
    </Box>
  );
}

export default function DietEditor({
  record,
  patientId,
  token,
  examinationId,
  canEdit = true,
  weightSeries,
  measurements,
  patient,
  onSaved,
  onError,
  onSetStatus,
  onPrint,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();
  const [updateDietPlan] = useUpdateDietPlanMutation();
  const [listFoods] = useListFoodsMutation();
  const [listDietTemplates] = useListDoctorDietTemplatesMutation();
  const [createDietTemplate, { isLoading: savingTemplate }] = useCreateDoctorDietTemplateMutation();
  const canReadTemplates = useHasPermission("doctorTemplates:read");
  const canManageTemplates = useHasPermission("doctorTemplates:manage");
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);

  const [picker, setPicker] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const openPicker = (key) => {
    setPicker(key);
    setPickerOpen(true);
  };

  const updateFn = useCallback(
    async (payload) => {
      const res = await updateDietPlan(payload).unwrap();
      return res?.data?.plan || res;
    },
    [updateDietPlan]
  );

  const editable = canEdit && record?.status !== "cancelled";

  const { fieldValues, setField, saveStatus } = useInlineAutosave({
    record,
    createMutation: async () => {
      throw new Error("Plan, Yeni plan butonuyla oluşturulur.");
    },
    updateMutation: updateFn,
    fieldsFromRecord: planToForm,
    shouldCreate: () => false,
    debounceMs: 700,
    manualCreate: false,
    buildPayload: (form) => ({
      token,
      patientId,
      ...(examinationId ? { examinationId } : {}),
      ...formToPayload(form),
    }),
    recordIdField: "planId",
    revertOnError: true,
    onSaved,
    onError,
    registerAutoSave,
    registerFlush,
  });

  useEffect(() => {
    if (typeof onSaveStatusChange === "function") onSaveStatusChange(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  const fetchFoods = useMemo(() => makeFetchPage(listFoods, token, pickFoods, foodOption), [listFoods, token]);
  const fetchTemplates = useMemo(
    () =>
      makeFetchPage(
        listDietTemplates,
        token,
        (d) => d.templates || [],
        (t) => ({
          key: String(t._id),
          value: t.name,
          title: t.name,
          subtitle: `${(t.days || []).length ? "Öğün planı · " : ""}${(t.allowedList || []).length} serbest · ${(t.forbiddenList || []).length} yasaklı`,
          raw: t,
        })
      ),
    [listDietTemplates, token]
  );

  // Şablondan doldur: öğün planı, hedefler ve listeler şablondan gelir; not boşsa o da.
  const applyTemplate = (t) => {
    if (!t) return;
    const fromTpl = planToForm({ ...t, title: fieldValues.title });
    if ((t.days || []).length) setField("days", fromTpl.days);
    if (t.targets) setField("targets", { ...fieldValues.targets, ...fromTpl.targets });
    if ((t.allowedList || []).length) setField("allowedList", fromTpl.allowedList);
    if ((t.forbiddenList || []).length) setField("forbiddenList", fromTpl.forbiddenList);
    if (t.note && !fieldValues.note) setField("note", t.note);
    showSuccess(`"${t.name}" şablonu uygulandı.`);
  };

  const saveAsTemplate = async (name) => {
    try {
      const body = formToPayload(fieldValues);
      await createDietTemplate({
        token,
        name,
        days: body.days,
        targets: body.targets,
        note: body.note,
        allowedList: body.allowedList,
        forbiddenList: body.forbiddenList,
      }).unwrap();
      setSaveTemplateOpen(false);
      showSuccess("Şablon kaydedildi.");
    } catch (err) {
      showError(err?.data?.message || "Şablon kaydedilemedi.");
    }
  };

  const foodSection = (key, title, color, sx) => {
    const items = fieldValues[key] || [];
    return (
      <Box sx={sx}>
        <SectionHeader
          title={title}
          count={items.length}
          addLabel="Besin Ekle"
          onAdd={editable ? () => openPicker(key) : undefined}
          palette={palette}
        />
        <Box sx={listAreaSx(FOOD_LIST_HEIGHT, items.length === 0)}>
          {items.length === 0 ? (
            <SectionEmpty
              icon={<RestaurantIcon />}
              title="Henüz besin eklenmedi"
              hint={editable ? "Besin Ekle ile listeden seçin." : undefined}
            />
          ) : (
            <FoodRows
              items={items}
              color={color}
              onRemove={editable ? (idx) => setField(key, items.filter((_, i) => i !== idx)) : undefined}
            />
          )}
        </Box>
      </Box>
    );
  };

  const activePicker = foodPickers(terms)[picker];
  const targets = fieldValues.targets || {};
  const hydrationOn = useClinicModule("hydration");
  const goal = fieldValues.goal || {};

  const statusActions = canEdit && onSetStatus ? (
    record?.status === "active" ? (
      <ActionButton variant="confirm" compact tone="soft" palette={palette} label="Tamamla" onClick={() => onSetStatus("completed")} />
    ) : (
      <ActionButton variant="edit" compact tone="soft" palette={palette} label="Aktif yap" onClick={() => onSetStatus("active")} />
    )
  ) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <EditorHeader
        record={record}
        newLabel="Beslenme planı"
        editLabel="Beslenme planı düzenleniyor"
        saveStatus={saveStatus}
        palette={palette}
        actions={
          <>
            {editable && canReadTemplates ? (
              <ActionButton variant="edit" compact tone="outline" palette={palette} label="Şablondan" onClick={() => setTemplatePickerOpen(true)} />
            ) : null}
            {canManageTemplates ? (
              <ActionButton variant="save" compact tone="outline" palette={palette} label="Şablon kaydet" onClick={() => setSaveTemplateOpen(true)} />
            ) : null}
            {statusActions}
            {onPrint ? <PdfButton onClick={() => onPrint(fieldValues)} title="Beslenme planı çıktısı (PDF)" palette={palette} /> : null}
          </>
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {/* Plan bilgisi */}
        <Box sx={{ ...SECTION_SX, display: "flex", flexDirection: "column", gap: 1 }}>
          <FieldRow label="Plan adı">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              value={fieldValues.title || ""}
              onChange={(e) => setField("title", e.target.value)}
              disabled={!editable}
            />
          </FieldRow>
          <FieldRow label="Tarih aralığı">
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              <FormDatePicker
                palette={palette}
                value={fieldValues.startDate || ""}
                onChange={(ymd) => setField("startDate", ymd)}
                disabled={!editable}
                sx={{ maxWidth: 180 }}
              />
              <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate400 }}>—</Typography>
              <FormDatePicker
                palette={palette}
                value={fieldValues.endDate || ""}
                onChange={(ymd) => setField("endDate", ymd)}
                disabled={!editable}
                sx={{ maxWidth: 180 }}
              />
            </Box>
          </FieldRow>
        </Box>

        {/* Günlük hedefler */}
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_HEADING_SX}>Günlük hedefler</Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {TARGET_FIELDS.map((f) => (
              <NumberField
                key={f.key}
                label={f.label}
                unit={f.unit}
                value={targets[f.key]}
                onChange={(e) => setField("targets", { ...targets, [f.key]: e.target.value })}
                disabled={!editable}
                palette={palette}
              />
            ))}
          </Box>
          {hydrationOn ? (
            // Plan aktifken su hedefi hastanın su takibine klinik hedefi olarak uygulanır (sunucu)
            <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate700 }}>Günlük su hedefi</Typography>
              <ChoiceChipGroup
                options={WATER_OPTIONS}
                value={targets.waterMl != null && targets.waterMl !== "" ? String(targets.waterMl) : null}
                onChange={(v) => setField("targets", { ...targets, waterMl: v })}
                disabled={!editable}
                palette={palette}
                ariaLabel="Günlük su hedefi"
              />
              <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
                Su hedefi plan aktifken {terms.patientGenLower} su takibine uygulanır.
              </Typography>
            </Box>
          ) : null}
        </Box>

        {/* Kilo hedefi */}
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_HEADING_SX}>Kilo hedefi</Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end", mb: 1.5 }}>
            <NumberField
              label="Başlangıç"
              unit="kg"
              value={goal.startWeight}
              onChange={(e) => setField("goal", { ...goal, startWeight: e.target.value })}
              disabled={!editable}
              palette={palette}
            />
            <NumberField
              label="Hedef"
              unit="kg"
              value={goal.targetWeight}
              onChange={(e) => setField("goal", { ...goal, targetWeight: e.target.value })}
              disabled={!editable}
              palette={palette}
            />
            <Box sx={{ minWidth: 180 }}>
              <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mb: 0.25 }}>Hedef tarihi</Typography>
              <FormDatePicker
                palette={palette}
                value={goal.targetDate || ""}
                onChange={(ymd) => setField("goal", { ...goal, targetDate: ymd })}
                disabled={!editable}
                sx={{ maxWidth: 180 }}
              />
            </Box>
          </Box>
          <WeightGoalProgress goal={goal} series={weightSeries} palette={palette} />
        </Box>

        {/* Vücut ölçüleri — plan başlangıcından bu yana değişim + bel hedefi */}
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_HEADING_SX}>Vücut ölçüleri</Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end", mb: 1.5 }}>
            <NumberField
              label="Bel başlangıç"
              unit="cm"
              value={goal.startWaist}
              onChange={(e) => setField("goal", { ...goal, startWaist: e.target.value })}
              disabled={!editable}
              palette={palette}
            />
            <NumberField
              label="Bel hedefi"
              unit="cm"
              value={goal.targetWaist}
              onChange={(e) => setField("goal", { ...goal, targetWaist: e.target.value })}
              disabled={!editable}
              palette={palette}
            />
          </Box>
          {Array.isArray(measurements) ? (
          <BodyMeasurementsSummary
            records={measurements}
            patient={patient}
            since={fieldValues.startDate || record?.createdAt}
            targetWaist={goal.targetWaist}
            startWaist={goal.startWaist}
            palette={palette}
            emptyText="Henüz mezura ölçüsü yok. Ölçümler sekmesinde “Elle ölçüm gir” ile bel, kalça gibi ölçüleri girin; değişim burada plan başlangıcına göre görünür."
          />
          ) : (
            <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
              Ölçü değişimi hastanın Beslenme Planı sekmesinde görünür.
            </Typography>
          )}
        </Box>

        {/* Öğün planı — plan türü burada seçilir: öğün planlı (diyetisyen öğün yazar) ya da
            serbest (öğün yok; hasta yediğini mobilden girer, günlük hedefler esas). */}
        <Box sx={SECTION_SX}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
            <Typography sx={{ ...SECTION_HEADING_SX, mb: 0 }}>Öğün planı</Typography>
            <ChoiceChipGroup
              options={PLAN_MODE_OPTIONS}
              size="field"
              value={fieldValues.mode || "meal_plan"}
              onChange={(v) => setField("mode", v)}
              disabled={!editable}
              palette={palette}
              ariaLabel="Plan türü"
            />
          </Box>
          {(fieldValues.mode || "meal_plan") === "meal_plan" ? (
            <MealPlanEditor
              days={fieldValues.days || []}
              onChange={(days) => setField("days", days)}
              targets={targets}
              canEdit={editable}
              palette={palette}
              token={token}
            />
          ) : (
            <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
              Öğün yazılmaz. {terms.patient} yediklerini mobil uygulamada katalogdan seçerek (bulamazsa kalorisini
              yazarak) girer; günlük hedefler esas alınır. Gün gün alım Beslenme › Takip bölümünde görünür.
            </Typography>
          )}
        </Box>

        {/* Serbest | Yasaklı besinler */}
        <Box sx={SPLIT_GRID_SX}>
          {foodSection("allowedList", "Serbest Besinler", COLORS.successDark, SPLIT_LEFT_SX)}
          {foodSection("forbiddenList", "Yasaklı Besinler", COLORS.errorDark, SPLIT_RIGHT_SX)}
        </Box>

        {/* Not */}
        <Box sx={{ ...SECTION_SX, borderBottom: "none" }}>
          <FieldRow label="Not">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              multiline
              minRows={3}
              placeholder={`${terms.patientDat} iletilecek notlar`}
              value={fieldValues.note || ""}
              onChange={(e) => setField("note", e.target.value)}
              disabled={!editable}
            />
          </FieldRow>
        </Box>
      </Box>

      <TagPickerDrawer
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        icon={<RestaurantIcon />}
        title="Şablondan doldur"
        subtitle="Öğün planı, hedefler ve besin listeleri şablondan gelir."
        fetchPage={fetchTemplates}
        single
        searchPlaceholder="Şablon ara"
        noun="şablon"
        onSubmit={(_, options) => {
          applyTemplate(options?.[0]?.raw);
          setTemplatePickerOpen(false);
        }}
      />

      <PromptDialog
        open={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        onConfirm={saveAsTemplate}
        title="Şablon olarak kaydet"
        message="Bu planın öğünleri, hedefleri ve besin listeleri şablon olarak saklanır."
        inputLabel="Şablon adı"
        initialValue={fieldValues.title || ""}
        required
        multiline={false}
        confirmText="Kaydet"
        confirmVariant="primary"
        loading={savingTemplate}
        loadingText="Kaydediliyor..."
      />

      <TagPickerDrawer
        open={pickerOpen && Boolean(activePicker)}
        onClose={() => setPickerOpen(false)}
        icon={<RestaurantIcon />}
        accent={activePicker?.accent}
        title={activePicker?.title}
        subtitle={activePicker?.subtitle}
        fetchPage={fetchFoods}
        existing={(picker ? fieldValues[picker] || [] : []).map((f) => f.name)}
        searchPlaceholder="Besin ara"
        noun="besin"
        onSubmit={(_, options) => {
          if (picker) setField(picker, appendFoods(fieldValues[picker], options.map((o) => o.raw)));
          setPickerOpen(false);
        }}
      />
    </Box>
  );
}
